import { Reference, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import RadioButtonGroup from '@components/fields/RadioButtonGroup';
import RadioGroup from '@components/fields/RadioGroup';
import Select from '@components/fields/Select';
import Form from '@components/forms/Form';
import {
  MedicationFragment as Medication,
  MedicationCreateDocument,
  MedicationCreateMutation,
  MedicationCreateMutationVariables,
  MedicationFormatEnum,
  MedicationFragmentDoc,
  MedicationKindEnum,
  MedicationPaymentTypeEnum,
  MedicationUpdateDocument,
  MedicationUpdateMutation,
  MedicationUpdateMutationVariables
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import {
  optionsForMedicationFormat,
  optionsForMedicationKind,
  optionsForMedicationPaymentType
} from '@utils/optionUtils';

type FormData = {
  dosage: string | null;
  format: MedicationFormatEnum | null;
  formatOther: string | null;
  instructions: string | null;
  kind: MedicationKindEnum | null;
  name: string;
  notes: string | null;
  paymentType: MedicationPaymentTypeEnum | null;
  pharmacy: string | null;
  prescriber: string | null;
  prescriptionNumber: string | null;
  reasonForTaking: string | null;
  refillFrequency: string | null;
};

type Props = {
  autofocus: boolean;
  journeyId: string;
  kind?: MedicationKindEnum;
  medication?: Medication;
  onCancel?: () => void;
  onError: () => void;
  onSuccess: () => void;
};

const MedicationForm = ({ autofocus, journeyId, kind, medication, onCancel, onError, onSuccess }: Props) => {
  const [medicationKind, setMedicationKind] = useState<MedicationKindEnum | undefined>(kind);

  const calculateInitialValues = useCallback(
    () => ({
      dosage: medication?.dosage ?? null,
      format: medication?.format.formatOption ?? null,
      formatOther: medication?.format.other ?? null,
      instructions: medication?.instructions ?? null,
      kind: medication?.kind ?? medicationKind ?? null,
      name: medication?.name ?? '',
      notes: medication?.notes ?? null,
      paymentType: medication?.paymentType ?? null,
      pharmacy: medication?.pharmacy ?? null,
      prescriber: medication?.prescriber ?? null,
      prescriptionNumber: medication?.prescriptionNumber ?? null,
      reasonForTaking: medication?.reasonForTaking ?? null,
      refillFrequency: medication?.refillFrequency ?? null
    }),
    [medication, medicationKind]
  );

  const initialValues = calculateInitialValues();
  const intl = useIntl();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createMedication] = useMutation<MedicationCreateMutation, MedicationCreateMutationVariables>(
    MedicationCreateDocument,
    {
      update: (store, { data }) => {
        if (data?.medicationCreate?.medication) {
          store.modify({
            fields: {
              medications(cachedMedicationRefs: readonly Reference[]) {
                const newMedicationRef = store.writeFragment({
                  data: data.medicationCreate?.medication,
                  fragment: MedicationFragmentDoc,
                  fragmentName: 'Medication'
                });
                if (newMedicationRef && data.medicationCreate?.medication) {
                  return [...cachedMedicationRefs, newMedicationRef];
                }
                return [...cachedMedicationRefs];
              }
            },
            id: store.identify({ __typename: 'Journey', id: journeyId })
          });
        }
      }
    }
  );
  const [updateMedication] = useMutation<MedicationUpdateMutation, MedicationUpdateMutationVariables>(
    MedicationUpdateDocument
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      dosage: yup.string().nullable().defined(),
      format: yup.string().nullable().defined().oneOf(Object.values(MedicationFormatEnum)),
      formatOther: yup.string().nullable().defined(),
      instructions: yup.string().nullable().defined(),
      kind: yup.string().nullable().defined().oneOf(Object.values(MedicationKindEnum)),
      name: yup.string().required(intl.formatMessage({ id: 'errors.medication.name.blank' })),
      notes: yup.string().nullable().defined(),
      paymentType: yup.string().nullable().defined().oneOf(Object.values(MedicationPaymentTypeEnum)),
      pharmacy: yup.string().nullable().defined(),
      prescriber: yup.string().nullable().defined(),
      prescriptionNumber: yup.string().nullable().defined(),
      reasonForTaking: yup.string().nullable().defined(),
      refillFrequency: yup.string().nullable().defined()
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const format = form.watch('format');
  const formKind = form.watch('kind');

  useEffect(() => {
    const values = calculateInitialValues();
    form.reset(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateInitialValues]);

  useEffect(() => {
    setMedicationKind(formKind ?? kind ?? medication?.kind ?? undefined);
  }, [formKind, kind, medication?.kind]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (medication?.id && medicationKind) {
      return updateMedication({
        variables: {
          ...form.getValues(),
          format: {
            formatOption: form.getValues().format,
            other: form.getValues().format === MedicationFormatEnum.other ? form.getValues().formatOther : null
          },
          id: medication.id,
          kind: medicationKind
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.medicationUpdate,
          intl,
          onError,
          onSuccess: () => {
            form.reset();
            onSuccess();
          },
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.medication'
        });
      });
    }
    if (medicationKind) {
      return createMedication({
        variables: {
          ...form.getValues(),
          format: {
            formatOption: form.getValues().format,
            other: form.getValues().format === MedicationFormatEnum.other ? form.getValues().formatOther : null
          },
          journeyId,
          kind: medicationKind
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.medicationCreate,
          intl,
          onError,
          onSuccess: () => {
            form.reset();
            onSuccess();
          },
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.medication'
        });
      });
    }
  }, [createMedication, medicationKind, journeyId, form, intl, medication, onError, onSuccess, updateMedication]);

  const paymentTypeOptions = optionsForMedicationPaymentType(intl);
  const formatOptions = optionsForMedicationFormat(intl);
  const medicationKindOptions = optionsForMedicationKind(intl);

  return (
    <Form data-test="medication-form" hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <Input
        autofocus={autofocus}
        control={form.control}
        data-test="nameField"
        error={form.formState.errors.name}
        label={intl.formatMessage({ id: 'forms.medication.nameLabel' })}
        name="name"
        required
      />
      {!kind && !medication && (
        <>
          <RadioGroup<FormData>
            control={form.control}
            error={form.formState.errors.kind}
            label={intl.formatMessage({ id: 'forms.medication.medicationTypeLabel' })}
            name="kind"
            options={medicationKindOptions}
            required
          />
        </>
      )}
      <Input
        control={form.control}
        error={form.formState.errors.reasonForTaking}
        label={intl.formatMessage({ id: 'forms.medication.reasonForTakingLabel' })}
        name="reasonForTaking"
      />
      <Input
        control={form.control}
        error={form.formState.errors.dosage}
        label={intl.formatMessage({ id: 'forms.medication.dosageLabel' })}
        name="dosage"
      />
      <Input
        control={form.control}
        error={form.formState.errors.instructions}
        label={intl.formatMessage({ id: 'forms.medication.instructionsLabel' })}
        name="instructions"
      />
      <Select
        control={form.control}
        data-test="format-input"
        error={form.formState.errors.format}
        label={intl.formatMessage({ id: 'forms.medication.formatLabel' })}
        name="format"
        options={formatOptions}
      />
      <div className={format !== MedicationFormatEnum.other ? 'ion-hide' : undefined}>
        <Input
          control={form.control}
          error={form.formState.errors.formatOther}
          label={intl.formatMessage({ id: 'forms.medication.formatOtherLabel' })}
          name="formatOther"
        />
      </div>
      {medicationKind === MedicationKindEnum.prescription && (
        <>
          <Input
            control={form.control}
            error={form.formState.errors.prescriber}
            label={intl.formatMessage({ id: 'forms.medication.prescriberLabel' })}
            name="prescriber"
          />
          <Input
            control={form.control}
            error={form.formState.errors.refillFrequency}
            label={intl.formatMessage({ id: 'forms.medication.refillFrequencyLabel' })}
            name="refillFrequency"
          />
          <Input
            control={form.control}
            error={form.formState.errors.pharmacy}
            label={intl.formatMessage({ id: 'forms.medication.pharmacyLabel' })}
            name="pharmacy"
          />
          <Input
            control={form.control}
            error={form.formState.errors.prescriptionNumber}
            label={intl.formatMessage({ id: 'forms.medication.prescriptionNumberLabel' })}
            name="prescriptionNumber"
          />
        </>
      )}
      <RadioButtonGroup
        control={form.control}
        error={form.formState.errors.paymentType}
        label={intl.formatMessage({ id: 'forms.medication.paymentTypeLabel' })}
        name="paymentType"
        options={paymentTypeOptions}
      />
      <Input
        control={form.control}
        error={form.formState.errors.notes}
        label={intl.formatMessage({ id: 'forms.medication.notesLabel' })}
        name="notes"
      />
      <FormButtons hookForm={form} onCancel={onCancel} saveDisabled={!medicationKind} saveKey="dictionary.save" />
    </Form>
  );
};

export default MedicationForm;
